import { compact } from "lodash";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
  AssessmentGroupDelivery,
  AssessmentGroupListFragmentFragment,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  ComplianceProgramCalibrationType,
  Maybe,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { ComboboxGenericOption } from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import DeprecatedSelect from "@components/select/select";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import InfoTooltip from "@components/tooltip/info-tooltip";
import WarningTooltip from "@components/tooltip/warning-tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { assessmentGroupDeliveryOptions } from "@helpers/constants";
import { assertNonNull } from "@helpers/helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import TalentIndicatorSidebar from "../talent-indicator-sidebar/talent-indicator-sidebar";
import {
  AssessmentTemplateExtraSettings,
  ComplianceProgramStep,
  ProgramForm,
} from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import CoreValueAssessmentSettingsModal from "./core-value-assessment-settings-modal";
import GoalAssessmentSettingsModal from "./goal-assessment-settings-modal";
import QuestionSetSelector from "./question-set-selector";
import RoleBasedAssessmentSettingsModal from "./role-based-assessment-settings-modal";

const SelfManagerAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  complianceProgramSteps,
  talentIndicatorData,
  hideSettings,
  onUpdateComplianceProgramSteps,
  onUpdateAssessmentTemplateSettings,
  onUpdateAssessmentTemplate,
  availableAssessmentGroups,
  onRemoveAssessmentTemplate,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  onUpdateProposedComplianceProgram,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  complianceProgramSteps: ComplianceProgramStep[];
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  hideSettings?: boolean;
  onUpdateComplianceProgramSteps: (steps: ComplianceProgramStep[]) => void;
  onUpdateAssessmentTemplateSettings: (
    assessmentType: AssessmentType,
  ) => (settings: AssessmentTemplateExtraSettings) => void;
  onUpdateAssessmentTemplate: (
    assessmentType: AssessmentType,
  ) => (opt: ComboboxGenericOption<number> | null) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onRemoveAssessmentTemplate: (assessmentType: AssessmentType) => void;
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (
    assessmentType: AssessmentType,
    assessmentGroupId: number,
  ) => void;
  onUpdateProposedComplianceProgram: Dispatch<SetStateAction<ProgramForm>>;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const isAdmin = isAdminVar();
  const hasGoalBasedAssessments = organization.featureFlags.goals;

  const [isRoleBasedAssessmentModalOpen, setIsRoleBasedAssessmentModalOpen] =
    useState(false);
  const [isGoalBasedAssessmentModalOpen, setIsGoalBasedAssessmentModalOpen] =
    useState(false);
  const [
    isCoreValueBasedAssessmentModalOpen,
    setIsCoreValueBasedAssessmentModalOpen,
  ] = useState(false);
  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [isTalentIndicatorSidebarOpen, setIsTalentIndicatorSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(
        ({ assessmentType }) => assessmentType === AssessmentType.Performance,
      )
      .filter((assessmentGroup) => !assessmentGroup.hasNpsQuestions)
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const selectedAssessmentTemplates = useMemo(
    () =>
      proposedComplianceProgram.assessmentTemplates
        ? compact(
            proposedComplianceProgram.assessmentTemplates as Maybe<AssessmentTemplateComplianceRequirement>[],
          )
        : [],
    [proposedComplianceProgram.assessmentTemplates],
  );

  const selectedPerformanceAssessmentTemplate = useMemo(() => {
    return selectedAssessmentTemplates.find(
      (template) => template.assessmentType === AssessmentType.Performance,
    );
  }, [selectedAssessmentTemplates]);

  const selectedAssessmentGroup = useMemo(() => {
    return selectedPerformanceAssessmentTemplate &&
      selectedPerformanceAssessmentTemplate.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) =>
            id === selectedPerformanceAssessmentTemplate.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, selectedPerformanceAssessmentTemplate]);

  const hasManagerQuestions = useMemo(() => {
    const assessmentGroupHasManagerQuestions =
      selectedAssessmentGroup && !selectedAssessmentGroup.isOnlySelfAssessment;
    const talentIndicatorsHaveManagerQuestions =
      selectedPerformanceAssessmentTemplate?.talentIndicatorConfig &&
      selectedPerformanceAssessmentTemplate.talentIndicatorConfig.talentIndicators.some(
        (talentIndicator) =>
          talentIndicator?.responses !==
          AssessmentQuestionResponses.SelfAssessmentOnly,
      );
    return (
      assessmentGroupHasManagerQuestions || talentIndicatorsHaveManagerQuestions
    );
  }, [
    selectedAssessmentGroup,
    selectedPerformanceAssessmentTemplate?.talentIndicatorConfig,
  ]);

  useEffect(() => {
    if (
      selectedPerformanceAssessmentTemplate?.questionSetIds &&
      selectedPerformanceAssessmentTemplate.questionSetIds.length > 0 &&
      !hasManagerQuestions &&
      selectedPerformanceAssessmentTemplate?.delivery ===
        AssessmentGroupDelivery.AdminApproval
    ) {
      onUpdateAssessmentTemplateSettings(AssessmentType.Performance)({
        delivery: AssessmentGroupDelivery.Partial,
      });
    }
    // update delivery options when selected questions set only self assessment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasManagerQuestions]);

  const performanceDeliveryOptions = useMemo(
    () =>
      assessmentGroupDeliveryOptions(
        label,
        AssessmentType.Performance,
        !!hasManagerQuestions,
      ),
    [label, hasManagerQuestions],
  );

  const applicableTalentIndicators = useMemo(() => {
    return talentIndicatorData
      ? talentIndicatorData.filter(
          (talentIndicator) => !talentIndicator.isManagerIndicator,
        )
      : [];
  }, [talentIndicatorData]);

  return (
    <>
      {isTalentIndicatorSidebarOpen && (
        <TalentIndicatorSidebar
          onClose={() => setIsTalentIndicatorSidebarOpen(false)}
          canUpdate={canUpdate}
          assessmentType={AssessmentType.Performance}
          talentIndicatorData={applicableTalentIndicators}
          talentIndicatorSetup={
            selectedPerformanceAssessmentTemplate?.talentIndicatorConfig ?? {
              talentIndicators: [],
            }
          }
          onUpdateTalentIndicators={(talentIndicatorConfig) => {
            onUpdateAssessmentTemplateSettings(AssessmentType.Performance)({
              talentIndicatorConfig,
            });
          }}
        />
      )}
      {isAssessmentGroupSidebarOpen && (
        <AssessmentGroupSidebar
          assessmentType={AssessmentType.Performance}
          assessmentGroupId={selectedAssessmentGroup?.id}
          assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
          onClose={() => {
            setAssessmentGroupToDuplicate(null);
            setIsAssessmentGroupSidebarOpen(false);
          }}
          onAssessmentGroupCreated={(assessmentGroup) => {
            onAssessmentGroupCreated(assessmentGroup);
            onUpdateAssessmentTemplate(AssessmentType.Performance)({
              value: assessmentGroup.id,
              label: assessmentGroup.title,
            });
          }}
          onAssessmentGroupArchived={(assessmentGroupId) =>
            onAssessmentGroupArchived(
              AssessmentType.Performance,
              assessmentGroupId,
            )
          }
        />
      )}
      {isRoleBasedAssessmentModalOpen &&
        selectedPerformanceAssessmentTemplate && (
          <RoleBasedAssessmentSettingsModal
            assessmentType={AssessmentType.Performance}
            onClose={() => setIsRoleBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={
              selectedPerformanceAssessmentTemplate
            }
            onChangeAssessmentTemplateSettings={onUpdateAssessmentTemplateSettings(
              AssessmentType.Performance,
            )}
          />
        )}
      {isGoalBasedAssessmentModalOpen &&
        selectedPerformanceAssessmentTemplate && (
          <GoalAssessmentSettingsModal
            assessmentType={AssessmentType.Performance}
            onClose={() => setIsGoalBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={
              selectedPerformanceAssessmentTemplate
            }
            onChangeAssessmentTemplateSettings={onUpdateAssessmentTemplateSettings(
              AssessmentType.Performance,
            )}
          />
        )}
      {isCoreValueBasedAssessmentModalOpen &&
        selectedPerformanceAssessmentTemplate && (
          <CoreValueAssessmentSettingsModal
            assessmentType={AssessmentType.Performance}
            onClose={() => setIsCoreValueBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={
              selectedPerformanceAssessmentTemplate
            }
            onChangeAssessmentTemplateSettings={onUpdateAssessmentTemplateSettings(
              AssessmentType.Performance,
            )}
          />
        )}
      <ComplianceProgramFormStep
        canDelete={canUpdate && !hideSettings}
        title={`Self and Manager ${label("review", {
          capitalize: true,
        })}`}
        onDelete={() => {
          onRemoveAssessmentTemplate(AssessmentType.Performance);
          onUpdateComplianceProgramSteps(
            complianceProgramSteps.filter(
              (s) => s !== ComplianceProgramStep.SELF_MANAGER_REVIEW,
            ),
          );
        }}
      >
        <div className="p-6 flex flex-col gap-3">
          <div className="text-sm text-gray-600 font-medium mb-4">
            Sections (For Form Input)
          </div>
          <div className="flex items-center gap-3 p-4 bg-white border rounded">
            <QuestionSetSelector
              assessmentType={AssessmentType.Performance}
              availableAssessmentGroupSelectOptions={
                availableAssessmentGroupSelectOptions
              }
              canUpdate={canUpdate}
              onUpdateAssessmentTemplate={onUpdateAssessmentTemplate}
              onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
              onOpenAssessmentGroupSidebar={() =>
                setIsAssessmentGroupSidebarOpen(true)
              }
              selectedAssessmentGroup={selectedAssessmentGroup}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-white border rounded">
            <div className="flex items-center gap-2">
              <div className="text-sm">{`Role ${label("review", {
                capitalize: true,
              })}`}</div>
              <InfoTooltip text="Use this if you would like to assess an individual based on their job responsibilities and competencies." />
            </div>

            {!organization.hasCareerFramework ? (
              <WarningTooltip
                size={6}
                text={`There are no Career Tracks defined for your ${label(
                  "organization",
                )}.${
                  isAdmin
                    ? ` Go to ${label("organization", {
                        capitalize: true,
                      })} settings to define them.`
                    : ""
                }`}
              />
            ) : (
              <div className="flex items-center gap-4">
                <Button
                  className="mb-1"
                  disabled={
                    !selectedPerformanceAssessmentTemplate ||
                    !selectedPerformanceAssessmentTemplate?.enableRoleBasedAssessments
                  }
                  icon={TopicflowIconMap.edit}
                  theme={buttonTheme.iconGray}
                  onClick={() => setIsRoleBasedAssessmentModalOpen(true)}
                />
                <ToggleSwitch
                  disabled={
                    !canUpdate || !selectedPerformanceAssessmentTemplate
                  }
                  checked={
                    !!selectedPerformanceAssessmentTemplate?.enableRoleBasedAssessments
                  }
                  onChange={(enabled) =>
                    onUpdateAssessmentTemplateSettings(
                      AssessmentType.Performance,
                    )({
                      enableRoleBasedAssessments: enabled,
                    })
                  }
                />
              </div>
            )}
          </div>

          {hasGoalBasedAssessments && (
            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">{`${label("goal", {
                  capitalize: true,
                })} ${label("review", {
                  capitalize: true,
                })}`}</div>
                <InfoTooltip
                  text={`Use this if you would like to assess an individual's progress towards their ${label(
                    "goal",
                    {
                      pluralize: true,
                    },
                  )}.`}
                />
              </div>

              <div className="flex items-center gap-4">
                <Button
                  className="mb-1"
                  disabled={
                    !selectedPerformanceAssessmentTemplate ||
                    !selectedPerformanceAssessmentTemplate?.enableGoalAssessments
                  }
                  icon={TopicflowIconMap.edit}
                  theme={buttonTheme.iconGray}
                  onClick={() => setIsGoalBasedAssessmentModalOpen(true)}
                />
                <ToggleSwitch
                  disabled={
                    !canUpdate || !selectedPerformanceAssessmentTemplate
                  }
                  checked={
                    !!selectedPerformanceAssessmentTemplate?.enableGoalAssessments
                  }
                  onChange={(enabled) =>
                    onUpdateAssessmentTemplateSettings(
                      AssessmentType.Performance,
                    )({
                      enableGoalAssessments: enabled,
                    })
                  }
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-4 bg-white border rounded">
            <div className="flex items-center gap-2">
              <div className="text-sm">{`Values ${label("review", {
                capitalize: true,
              })}`}</div>
              <InfoTooltip text="Use this if you would like to assess an individual's alignment with your Core Values" />
            </div>

            {organization.coreValues?.edges.length === 0 ? (
              <WarningTooltip
                size={6}
                text={`There are no Core Values defined for your ${label(
                  "organization",
                )}.${
                  isAdmin
                    ? ` Go to ${label("organization", {
                        capitalize: true,
                      })} settings to define them.`
                    : ""
                }`}
              />
            ) : (
              <div className="flex items-center gap-4">
                <Button
                  className="mb-1"
                  disabled={
                    !selectedPerformanceAssessmentTemplate ||
                    !selectedPerformanceAssessmentTemplate?.enableCoreValueAssessments
                  }
                  icon={TopicflowIconMap.edit}
                  theme={buttonTheme.iconGray}
                  onClick={() => setIsCoreValueBasedAssessmentModalOpen(true)}
                />
                <ToggleSwitch
                  disabled={
                    !canUpdate || !selectedPerformanceAssessmentTemplate
                  }
                  checked={
                    !!selectedPerformanceAssessmentTemplate?.enableCoreValueAssessments
                  }
                  onChange={(enabled) =>
                    onUpdateAssessmentTemplateSettings(
                      AssessmentType.Performance,
                    )({
                      enableCoreValueAssessments: enabled,
                    })
                  }
                />
              </div>
            )}
          </div>

          {waffle.flag_is_active("talent-indicators") && (
            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">Employee Talent Indicators</div>
                <InfoTooltip text="Use this if you would like to assess employees on standard talent indicators." />
              </div>

              {applicableTalentIndicators.length === 0 ? (
                <WarningTooltip
                  size={6}
                  text="There was a problem loading talent indicators"
                />
              ) : (
                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    aria-label="Edit employee talent indicators"
                    disabled={
                      !selectedPerformanceAssessmentTemplate ||
                      !selectedPerformanceAssessmentTemplate?.talentIndicatorConfig ||
                      selectedPerformanceAssessmentTemplate
                        ?.talentIndicatorConfig.talentIndicators?.length === 0
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsTalentIndicatorSidebarOpen(true)}
                  />
                  <ToggleSwitch
                    aria-label="Employee talent indicator toggle"
                    disabled={
                      !canUpdate || !selectedPerformanceAssessmentTemplate
                    }
                    checked={
                      !!selectedPerformanceAssessmentTemplate?.talentIndicatorConfig &&
                      selectedPerformanceAssessmentTemplate
                        .talentIndicatorConfig.talentIndicators.length > 0
                    }
                    onChange={(enabled) => {
                      onUpdateAssessmentTemplateSettings(
                        AssessmentType.Performance,
                      )({
                        talentIndicatorConfig: enabled
                          ? {
                              talentIndicators: applicableTalentIndicators.map(
                                (talentIndicator) => ({
                                  talentIndicatorId: talentIndicator.id,
                                  responses:
                                    AssessmentQuestionResponses.ExcludeSelfAssessment,
                                  responseVisibility:
                                    AssessmentQuestionResponseVisibility.HiddenFromSubject,
                                  comment: AssessmentQuestionComment.Optional,
                                  description: talentIndicator.description,
                                }),
                              ),
                            }
                          : { talentIndicators: [] },
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {!hideSettings && (
          <div className="p-6 border-t">
            <div className="text-sm text-gray-600 font-medium mb-4">
              Settings
            </div>
            <FormFields
              fields={[
                {
                  title: "Anonymity",
                  render: () => (
                    <div className="text-sm h-10 flex items-center">
                      Not anonymous
                    </div>
                  ),
                },
                {
                  title: "Delivery",
                  render: () =>
                    selectedPerformanceAssessmentTemplate ? (
                      <DeprecatedSelect<AssessmentGroupDelivery>
                        disabled={!canUpdate}
                        value={assertNonNull(
                          selectedPerformanceAssessmentTemplate.delivery,
                        )}
                        options={performanceDeliveryOptions}
                        onChange={(opt) => {
                          onUpdateAssessmentTemplateSettings(
                            AssessmentType.Performance,
                          )({
                            delivery: opt.value,
                          });
                          onUpdateProposedComplianceProgram((prev) => ({
                            ...prev,
                            calibration:
                              opt.value ===
                              AssessmentGroupDelivery.AdminApproval
                                ? ComplianceProgramCalibrationType.PostCalibration
                                : ComplianceProgramCalibrationType.Off,
                          }));
                        }}
                      />
                    ) : (
                      <div className="text-sm h-10 flex items-center">-</div>
                    ),
                },
              ]}
            />
          </div>
        )}
      </ComplianceProgramFormStep>
    </>
  );
};
export default SelfManagerAssessmentStep;
